/**
 * 航拍 GLB 查看器（交互对齐地图习惯）
 * - 左键拖：平移
 * - 右键拖 / Ctrl+左键拖：任意角度旋转（左右=绕竖直轴，上下=仰俯；始终按当前画面方向）
 * - Shift+左键拖：框选放大
 * - 滚轮：缩放
 */
import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

let sharedDraco = null;
function getDracoLoader() {
  if (!sharedDraco) {
    sharedDraco = new DRACOLoader();
    sharedDraco.setDecoderPath(`${process.env.PUBLIC_URL || ''}/draco/gltf/`);
    sharedDraco.setDecoderConfig({ type: 'js' });
  }
  return sharedDraco;
}

/** ODM 常为 Z-up → 转到 Y-up，便于「绕竖直轴」转盘 */
function orientModelYUp(root) {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  if (size.z <= size.x && size.z <= size.y) {
    root.rotation.x = -Math.PI / 2;
  } else if (size.x <= size.y && size.x <= size.z) {
    root.rotation.z = Math.PI / 2;
  }
  root.updateMatrixWorld(true);
  const box2 = new THREE.Box3().setFromObject(root);
  root.position.sub(box2.getCenter(new THREE.Vector3()));
  return box2.getSize(new THREE.Vector3());
}

/**
 * @param {{ url: string, height?: number }} props
 */
export default function OrthoGlbViewer({ url, height = 520 }) {
  const mountRef = useRef(null);
  const boxRef = useRef(null);
  const [status, setStatus] = useState('加载中…');
  const [error, setError] = useState('');

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !url) return undefined;

    let disposed = false;
    let raf = 0;
    let renderer;
    let resizeObs;
    let modelRoot = null;

    setStatus('加载中…');
    setError('');

    const w0 = Math.max(mount.clientWidth || 760, 320);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a1a);

    const camera = new THREE.PerspectiveCamera(45, w0 / height, 0.01, 8000);
    const target = new THREE.Vector3(0, 0, 0);
    // 球坐标：phi=0 顶视，phi=π/2 平视；用球坐标保证转到背面后「上拖」方向仍与当前画面一致
    const spherical = {
      theta: 0.55,
      phi: 0.95,
      radius: 80
    };
    let minRadius = 1;
    let maxRadius = 5000;

    const updateCamera = () => {
      const phi = THREE.MathUtils.clamp(spherical.phi, 0.08, Math.PI - 0.08);
      spherical.phi = phi;
      const sinPhi = Math.sin(phi);
      camera.position.set(
        target.x + spherical.radius * sinPhi * Math.sin(spherical.theta),
        target.y + spherical.radius * Math.cos(phi),
        target.z + spherical.radius * sinPhi * Math.cos(spherical.theta)
      );
      camera.lookAt(target);
      camera.near = Math.max(spherical.radius / 250, 0.01);
      camera.far = Math.max(spherical.radius * 50, 2000);
      camera.updateProjectionMatrix();
    };
    updateCamera();

    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w0, height, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    const canvas = renderer.domElement;
    Object.assign(canvas.style, {
      width: '100%',
      height: `${height}px`,
      display: 'block',
      touchAction: 'none',
      outline: 'none',
      userSelect: 'none',
      cursor: 'grab'
    });
    mount.innerHTML = '';
    mount.appendChild(canvas);

    scene.add(new THREE.HemisphereLight(0xffffff, 0x445566, 1.15));
    const dir = new THREE.DirectionalLight(0xffffff, 1.05);
    dir.position.set(50, 80, 40);
    scene.add(dir);
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));

    const drag = {
      active: false,
      mode: 'pan', // pan | rotate | box
      x: 0,
      y: 0,
      startX: 0,
      startY: 0,
      pointerId: null
    };

    const setBoxEl = (visible, x0, y0, x1, y1) => {
      const el = boxRef.current;
      if (!el) return;
      if (!visible) {
        el.style.display = 'none';
        return;
      }
      const rect = mount.getBoundingClientRect();
      const l = Math.min(x0, x1) - rect.left;
      const t = Math.min(y0, y1) - rect.top;
      const w = Math.abs(x1 - x0);
      const h = Math.abs(y1 - y0);
      el.style.display = 'block';
      el.style.left = `${l}px`;
      el.style.top = `${t}px`;
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
    };

    /** 将屏幕像素位移转为沿视线平面的平移（与地图左键平移手感接近） */
    const panByPixels = (dx, dy) => {
      const dist = spherical.radius;
      const vFov = (camera.fov * Math.PI) / 180;
      const viewH = 2 * Math.tan(vFov / 2) * dist;
      const viewW = viewH * camera.aspect;
      const rect = canvas.getBoundingClientRect();
      const sx = viewW / Math.max(rect.width, 1);
      const sy = viewH / Math.max(rect.height, 1);
      const right = new THREE.Vector3();
      const up = new THREE.Vector3();
      camera.matrixWorld.extractBasis(right, up, new THREE.Vector3());
      // 左拖：画面内容跟手（与地图一致）
      target.addScaledVector(right, -dx * sx);
      target.addScaledVector(up, dy * sy);
    };

    /** Shift 框选放大：以框中心为新观察点，按框相对视口缩小距离 */
    const applyBoxZoom = (x0, y0, x1, y1) => {
      const rect = canvas.getBoundingClientRect();
      const bw = Math.abs(x1 - x0);
      const bh = Math.abs(y1 - y0);
      if (bw < 8 || bh < 8) return;
      const cx = (x0 + x1) / 2;
      const cy = (y0 + y1) / 2;
      const ndcX = ((cx - rect.left) / rect.width) * 2 - 1;
      const ndcY = -(((cy - rect.top) / rect.height) * 2 - 1);
      const hit = new THREE.Vector3(ndcX, ndcY, 0.5).unproject(camera);
      const dirCam = hit.sub(camera.position).normalize();
      // 与目标平面（过 target、法线≈相机朝向）求交
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
        camera.getWorldDirection(new THREE.Vector3()),
        target
      );
      const ray = new THREE.Ray(camera.position, dirCam);
      const pts = new THREE.Vector3();
      if (ray.intersectPlane(plane, pts)) {
        target.copy(pts);
      }
      const scale = Math.max(bw / rect.width, bh / rect.height);
      spherical.radius = THREE.MathUtils.clamp(
        spherical.radius * THREE.MathUtils.clamp(scale * 1.05, 0.12, 0.92),
        minRadius,
        maxRadius
      );
      updateCamera();
    };

    const resolveMode = (e) => {
      if (e.shiftKey && e.button === 0) return 'box';
      if (e.button === 2) return 'rotate';
      if (e.button === 0 && e.ctrlKey) return 'rotate';
      if (e.button === 0) return 'pan';
      if (e.button === 1) return 'pan';
      return 'pan';
    };

    const onPointerDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      drag.active = true;
      drag.pointerId = e.pointerId;
      drag.x = e.clientX;
      drag.y = e.clientY;
      drag.startX = e.clientX;
      drag.startY = e.clientY;
      drag.mode = resolveMode(e);
      canvas.setPointerCapture?.(e.pointerId);
      canvas.style.cursor = drag.mode === 'pan' ? 'grabbing' : (drag.mode === 'box' ? 'crosshair' : 'move');
      if (drag.mode === 'box') {
        setBoxEl(true, drag.startX, drag.startY, drag.startX, drag.startY);
      }
    };

    const onPointerMove = (e) => {
      if (!drag.active || (drag.pointerId != null && e.pointerId !== drag.pointerId)) return;
      e.preventDefault();
      e.stopPropagation();
      const dx = e.clientX - drag.x;
      const dy = e.clientY - drag.y;
      drag.x = e.clientX;
      drag.y = e.clientY;

      if (drag.mode === 'box') {
        setBoxEl(true, drag.startX, drag.startY, e.clientX, e.clientY);
        return;
      }
      if (drag.mode === 'pan') {
        panByPixels(dx, dy);
        updateCamera();
        return;
      }
      // rotate：球坐标 —— 转到背面后上下方向仍相对「当前画面」一致
      // 左右拖：绕竖直轴（theta）
      spherical.theta -= dx * 0.0075;
      // 上拖(dy<0)：近景上移、远景下移 → phi 增大（更接近平视/俯冲感与地图 pitch 一致）
      spherical.phi -= dy * 0.0075;
      updateCamera();
    };

    const endDrag = (e) => {
      if (drag.pointerId != null && e.pointerId !== drag.pointerId) return;
      if (drag.mode === 'box' && drag.active) {
        applyBoxZoom(drag.startX, drag.startY, e.clientX, e.clientY);
      }
      drag.active = false;
      drag.pointerId = null;
      setBoxEl(false);
      canvas.style.cursor = 'grab';
      try {
        canvas.releasePointerCapture?.(e.pointerId);
      } catch (_) { /* ignore */ }
    };

    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const factor = Math.exp(e.deltaY * 0.0018);
      spherical.radius = THREE.MathUtils.clamp(
        spherical.radius * factor,
        minRadius,
        maxRadius
      );
      updateCamera();
    };

    const onContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('contextmenu', onContextMenu);
    canvas.addEventListener('mousedown', (e) => e.stopPropagation());

    const loader = new GLTFLoader();
    loader.setDRACOLoader(getDracoLoader());
    loader.load(
      url,
      (gltf) => {
        if (disposed) return;
        const root = gltf.scene || gltf.scenes?.[0];
        if (!root) {
          setError('GLB 内无场景');
          setStatus('');
          return;
        }
        const size = orientModelYUp(root);
        modelRoot = root;
        scene.add(root);
        const maxDim = Math.max(size.x, size.y, size.z, 1);
        // 默认更近：接近「铺满」观感（原先 1.85 偏小）
        spherical.radius = maxDim * 0.95;
        minRadius = maxDim * 0.08;
        maxRadius = maxDim * 12;
        spherical.theta = 0.55;
        spherical.phi = 0.92;
        target.set(0, 0, 0);
        updateCamera();
        setStatus('');
      },
      (ev) => {
        if (!ev.total) return;
        setStatus(`加载中 ${Math.round((ev.loaded / ev.total) * 100)}%`);
      },
      (err) => {
        if (disposed) return;
        console.error('[OrthoGlbViewer]', err);
        setError(err?.message || 'GLB 加载失败');
        setStatus('');
      }
    );

    const onResize = () => {
      if (disposed || !mount) return;
      const w = Math.max(mount.clientWidth || w0, 320);
      camera.aspect = w / height;
      camera.updateProjectionMatrix();
      renderer.setSize(w, height, false);
    };
    resizeObs = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    resizeObs?.observe(mount);
    window.addEventListener('resize', onResize);

    const tick = () => {
      if (disposed) return;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      resizeObs?.disconnect();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', endDrag);
      canvas.removeEventListener('pointercancel', endDrag);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('contextmenu', onContextMenu);
      renderer?.dispose();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose?.();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => {
            m.map?.dispose?.();
            m.dispose?.();
          });
        }
      });
      if (canvas?.parentNode === mount) mount.removeChild(canvas);
      modelRoot = null;
    };
  }, [url, height]);

  return (
    <div style={{ position: 'relative', width: '100%', touchAction: 'none', userSelect: 'none' }}>
      <div ref={mountRef} style={{ width: '100%', height, background: '#1a1a1a' }} />
      <div
        ref={boxRef}
        style={{
          display: 'none',
          position: 'absolute',
          border: '1.5px solid #38bdf8',
          background: 'rgba(56,189,248,0.15)',
          pointerEvents: 'none',
          zIndex: 2
        }}
      />
      {(status || error) && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: error ? '#fca5a5' : '#e5e7eb',
            background: 'rgba(0,0,0,0.35)',
            pointerEvents: 'none',
            fontSize: 14,
            zIndex: 3
          }}
        >
          {error || status}
        </div>
      )}
    </div>
  );
}
